import { useState } from "react";
import { MapPin, Phone, Mail, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../../i18n/LanguageContext";
import { Reveal, Overline } from "./Reveal";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

const inputCls =
  "rounded-none border-zinc-300 bg-white h-12 focus-visible:ring-1 focus-visible:ring-nero-ink focus-visible:border-nero-ink";

export const Contact = () => {
  const { t } = useLang();
  const c = t.contact;
  const f = c.form;
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", interest: "", message: "" });
  const [sending, setSending] = useState(false);
  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          access_key: process.env.REACT_APP_WEB3FORMS_ACCESS_KEY || "391350fc-5b46-4485-88fc-659850b655f9",
          subject: "Neue Projektanfrage – NeroChar Website",
          from_name: form.name || "NeroChar Website",
          name: form.name,
          company: form.company,
          email: form.email,
          phone: form.phone,
          interest: form.interest,
          message: form.message,
        }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success(f.success);
        setForm({ name: "", company: "", email: "", phone: "", interest: "", message: "" });
      } else {
        toast.error(f.error);
      }
    } catch (err) {
      toast.error(f.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="bg-white border-b border-border" data-testid="contact">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left: details */}
          <div className="lg:col-span-5">
            <Reveal>
              <Overline index="09">{c.label}</Overline>
              <h2 className="font-heading text-3xl sm:text-4xl font-medium tracking-tight text-nero-ink leading-[1.1]">
                {c.title}
              </h2>
              <p className="mt-6 font-body text-base lg:text-lg leading-relaxed text-zinc-600">{c.subtitle}</p>

              <div className="mt-10 border-t border-border pt-8">
                <div className="font-body text-xs uppercase tracking-[0.22em] font-semibold text-zinc-500 mb-5">
                  {c.companyTitle}
                </div>
                <div className="space-y-4">
                  <div data-testid="contact-company">
                    <div className="font-heading text-lg font-medium text-nero-ink">NeroChar</div>
                    <div className="font-body text-sm text-zinc-500">powered by SRC Global GmbH</div>
                  </div>
                  <InfoRow icon={MapPin} text={`${c.address1}, ${c.address2}`} testid="contact-address" />
                  <InfoRow icon={Phone} text={c.phone} href={`tel:${c.phone.replace(/\s/g, "")}`} testid="contact-phone" />
                  <InfoRow icon={Mail} text={c.email} href={`mailto:${c.email}`} testid="contact-email" />
                </div>
              </div>
            </Reveal>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal delay={0.1}>
              <form onSubmit={onSubmit} className="border border-border p-7 lg:p-10" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label={f.name}>
                    <Input data-testid="form-name" required value={form.name} onChange={update("name")} placeholder={f.namePh} className={inputCls} />
                  </Field>
                  <Field label={f.company}>
                    <Input data-testid="form-company" value={form.company} onChange={update("company")} placeholder={f.companyPh} className={inputCls} />
                  </Field>
                  <Field label={f.email}>
                    <Input data-testid="form-email" type="email" required value={form.email} onChange={update("email")} placeholder={f.emailPh} className={inputCls} />
                  </Field>
                  <Field label={f.phone}>
                    <Input data-testid="form-phone" value={form.phone} onChange={update("phone")} placeholder={f.phonePh} className={inputCls} />
                  </Field>
                </div>

                <div className="mt-5">
                  <Field label={f.interest}>
                    <Select value={form.interest} onValueChange={(v) => setForm((s) => ({ ...s, interest: v }))}>
                      <SelectTrigger data-testid="form-interest" className="rounded-none border-zinc-300 bg-white h-12 focus:ring-1 focus:ring-nero-ink data-[placeholder]:text-zinc-400">
                        <SelectValue placeholder={f.interest} />
                      </SelectTrigger>
                      <SelectContent className="rounded-none">
                        {f.interestOptions.map((opt, i) => (
                          <SelectItem key={i} value={opt} data-testid={`interest-option-${i}`}>{opt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>

                <div className="mt-5">
                  <Field label={f.message}>
                    <Textarea data-testid="form-message" required rows={5} value={form.message} onChange={update("message")} placeholder={f.messagePh} className="rounded-none border-zinc-300 bg-white focus-visible:ring-1 focus-visible:ring-nero-ink focus-visible:border-nero-ink resize-none" />
                  </Field>
                </div>

                <button
                  type="submit"
                  disabled={sending}
                  className="group mt-8 w-full inline-flex items-center justify-center gap-2.5 bg-nero-ink text-white font-medium px-7 py-4 hover:bg-zinc-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="form-submit"
                >
                  {sending ? f.sending : f.submit}
                  {!sending && <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={1.75} />}
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, children }) => (
  <div className="space-y-2">
    <Label className="font-body text-xs font-medium uppercase tracking-wider text-zinc-500">{label}</Label>
    {children}
  </div>
);

const InfoRow = ({ icon: Icon, text, href, testid }) => {
  const content = (
    <div className="flex items-center gap-3" data-testid={testid}>
      <Icon className="h-5 w-5 text-nero-green shrink-0" strokeWidth={1.5} />
      <span className="font-body text-base text-zinc-700">{text}</span>
    </div>
  );
  return href ? (
    <a href={href} className="block hover:text-nero-green transition-colors">{content}</a>
  ) : (
    content
  );
};
